import React, { useContext, useEffect, useMemo, useState } from "react";
import UserAccessControlUtils from "../utils/UserAccessControlUtils";
import { AuthContext } from "./AuthProvider";
import { queryClient } from "../App";
import ProgressIndicator from "../themes/components/ProgressIndicator";
import { useEnabledFeatureFlag } from "../common/feature-flagging/FeatureFlagProvider";

type AccessPermissionContextType = {
  hasPermissions: boolean;
};
export const AccessPermissionContext =
  React.createContext<AccessPermissionContextType>({ hasPermissions: false });

type Props = {
  path: string;
  requiredFeature: string | string[];
  requiredPermissions: string[];
  requiredCapability?: string | string[];
  requiredFeatureFlag?: string;
  isFeatureFlagged?: boolean;
};

const AccessPermissionsProvider: React.FC<Props> = ({
  children,
  requiredFeature,
  requiredPermissions,
  requiredCapability,
  requiredFeatureFlag,
  isFeatureFlagged = false,
  path,
}) => {
  const auth = useContext(AuthContext);
  const [hasPermission, setHasPermissions] = useState<boolean>(false);
  const [permissionChecked, setPermissionChecked] = useState<boolean>(false);

  // Check feature flag if required
  const featureFlagEnabled = useEnabledFeatureFlag(requiredFeatureFlag || "");
  const passesFeatureFlagCheck = !isFeatureFlagged || featureFlagEnabled;

  const contextData = useMemo(
    () => ({ hasPermissions: hasPermission }),
    [hasPermission]
  );

  const checkHasPermissions = (): void => {
    if (auth) {
      const hasRequiredFeatures = UserAccessControlUtils.hasFeature(
        auth.user_features,
        requiredFeature
      );
      const hasRequiredPermissions = UserAccessControlUtils.hasPermission(
        auth.permissions,
        requiredPermissions
      );
      const hasRequiredCapability = requiredCapability
        ? UserAccessControlUtils.hasCapability(
            auth.ta_capabilities,
            requiredCapability
          )
        : true;

      // User must have features, permissions, capability (if required),
      // AND pass feature flag check
      if (
        hasRequiredFeatures &&
        hasRequiredPermissions &&
        hasRequiredCapability &&
        passesFeatureFlagCheck
      ) {
        setHasPermissions(true);
      } else {
        setHasPermissions(false);
      }
    } else {
      setHasPermissions(false); // auth is null or undefined
    }

    // permissions have been checked
    setPermissionChecked(true);
  };

  // Refetch the auth profile only when navigating to a new route — keeping
  // this out of the permission-check effect below avoids an infinite refetch
  // loop when auth-derived deps (new array refs each fetch) change.
  useEffect(() => {
    queryClient.refetchQueries(["AuthUserProfileApiService"]);
  }, [path]);

  // Re-run the permission check whenever auth-derived gating inputs hydrate
  // so hasPermissions reflects the latest capabilities / features / perms.
  useEffect(() => {
    checkHasPermissions();
  }, [
    path,
    passesFeatureFlagCheck,
    auth?.ta_capabilities,
    auth?.user_features,
    auth?.permissions,
  ]);

  if (!permissionChecked) {
    // permission check is in progress
    return <ProgressIndicator />;
  }

  return (
    <AccessPermissionContext.Provider value={contextData}>
      {children}
    </AccessPermissionContext.Provider>
  );
};

export default AccessPermissionsProvider;
